<script setup>
import { computed } from 'vue'

/**
 * RunicAstrolabe — massive line-art instrument behind the Event
 * Passport chapter, sibling in spirit to the hero's Yggdrasil.
 *
 * An abstract astrolabe: graduated outer ring, dashed orbits, a rete of
 * crossing ellipses with star pointers, a reading arm — and the four
 * step-runes (Ansuz, Raidō, Othala, Wunjō) as cardinal points, the
 * active one glowing. Driven by the parent's ScrollTrigger:
 *  - `progress` (0→1) rotates the rings (outer clockwise, rete counter-
 *    clockwise, arm faster) — the instrument "reads" the journey.
 *  - `step` highlights the cardinal rune of the active step.
 *
 * Stroke-only, gold/magenta/violet on deep night, glow via drop-shadow.
 * Decorative: aria-hidden, pointer-events-none, low opacity.
 */
const props = defineProps({
  progress: { type: Number, default: 0 },
  step: { type: Number, default: 0 }
})

const C = 400 // center

// --- graduations of the outer ring (every 5°, long tick every 30°)
const ticks = Array.from({ length: 72 }, (_, i) => {
  const a = (i * 5 * Math.PI) / 180
  const long = i % 6 === 0
  const r0 = long ? 348 : 358
  const r1 = 370
  return {
    x1: C + Math.cos(a) * r0,
    y1: C + Math.sin(a) * r0,
    x2: C + Math.cos(a) * r1,
    y2: C + Math.sin(a) * r1,
    long
  }
})

// --- star pointers of the rete (curved spikes ending on a star dot)
const pointers = [
  { a: 15, r: 218 },
  { a: 76, r: 152 },
  { a: 128, r: 236 },
  { a: 196, r: 188 },
  { a: 262, r: 158 },
  { a: 318, r: 242 }
].map(({ a, r }) => {
  const rad = (a * Math.PI) / 180
  const x0 = C + Math.cos(rad) * (r - 52)
  const y0 = C + Math.sin(rad) * (r - 52)
  const x1 = C + Math.cos(rad) * r
  const y1 = C + Math.sin(rad) * r
  // slight curvature, perpendicular offset at the midpoint
  const mx = (x0 + x1) / 2 + Math.cos(rad + Math.PI / 2) * 10
  const my = (y0 + y1) / 2 + Math.sin(rad + Math.PI / 2) * 10
  return { d: `M${x0.toFixed(1)} ${y0.toFixed(1)} Q${mx.toFixed(1)} ${my.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`, x: x1, y: y1 }
})

// --- the four step-runes as cardinal points (top, right, bottom, left)
const RUNE_LINES = [
  ['M50 8 L50 112', 'M50 22 L86 46', 'M50 48 L86 72'], // Ansuz — the message
  ['M32 8 L32 112', 'M32 8 L72 32 L32 56', 'M32 56 L78 112'], // Raidō — the journey
  ['M50 8 L80 42 L50 76 L20 42 Z', 'M34 60 L12 112', 'M66 60 L88 112'], // Othala — belonging
  ['M36 8 L36 112', 'M36 8 L74 32 L36 56'] // Wunjō — joy
]
const runes = RUNE_LINES.map((lines, i) => {
  const a = ((i * 90 - 90) * Math.PI) / 180 // top, right, bottom, left
  return {
    lines,
    transform: `translate(${(C + Math.cos(a) * 312).toFixed(1)} ${(C + Math.sin(a) * 312).toFixed(1)}) scale(0.42) translate(-50 -60)`
  }
})

// --- scroll-linked rotations (the parent scrubs `progress`)
const outerStyle = computed(() => ({ transform: `rotate(${props.progress * 40}deg)` }))
const reteStyle = computed(() => ({ transform: `rotate(${props.progress * -60}deg)` }))
const armStyle = computed(() => ({ transform: `rotate(${14 + props.progress * 110}deg)` }))
</script>

<template>
  <svg
    viewBox="0 0 800 800"
    fill="none"
    aria-hidden="true"
    class="astrolabe"
  >
    <defs>
      <linearGradient id="astro-gold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#ffd9a2" />
        <stop offset="0.5" stop-color="#f0a75c" />
        <stop offset="1" stop-color="#d75fe8" />
      </linearGradient>
    </defs>

    <!-- fixed cross-hairs, weakest layer -->
    <g class="s-violet" opacity="0.35" stroke-width="0.7">
      <path d="M400 14 L400 786" />
      <path d="M14 400 L786 400" />
      <circle cx="400" cy="400" r="120" stroke-dasharray="2 7" />
    </g>

    <!-- ===== outer ring: graduations, orbits, cardinal runes ===== -->
    <g class="ring" :style="outerStyle">
      <circle cx="400" cy="400" r="382" class="s-gold" stroke-width="1.4" />
      <circle cx="400" cy="400" r="372" class="s-gold" stroke-width="0.7" opacity="0.7" />
      <line
        v-for="(t, i) in ticks"
        :key="i"
        :x1="t.x1"
        :y1="t.y1"
        :x2="t.x2"
        :y2="t.y2"
        class="s-gold"
        :stroke-width="t.long ? 1.3 : 0.7"
        :opacity="t.long ? 0.9 : 0.5"
      />
      <circle cx="400" cy="400" r="336" class="s-violet" stroke-width="0.8" stroke-dasharray="1 10" />

      <g
        v-for="(rune, i) in runes"
        :key="i"
        :transform="rune.transform"
        class="rune"
        :class="i === step ? 's-magenta rune-active' : 's-violet'"
      >
        <path v-for="(d, j) in rune.lines" :key="j" :d="d" stroke-width="4" />
      </g>
    </g>

    <!-- ===== rete: crossing orbits + star pointers, counter-rotating ===== -->
    <g class="ring" :style="reteStyle">
      <ellipse cx="400" cy="400" rx="262" ry="148" transform="rotate(28 400 400)" class="s-violet" stroke-width="0.9" />
      <ellipse cx="400" cy="400" rx="262" ry="148" transform="rotate(-32 400 400)" class="s-violet" stroke-width="0.9" opacity="0.75" />
      <circle cx="400" cy="400" r="196" class="s-magenta" stroke-width="0.8" opacity="0.8" />
      <circle cx="400" cy="400" r="264" class="s-violet" stroke-width="0.6" stroke-dasharray="14 9" opacity="0.6" />

      <g class="s-gold">
        <template v-for="(p, i) in pointers" :key="i">
          <path :d="p.d" stroke-width="1" />
          <circle :cx="p.x" :cy="p.y" r="2.6" fill="#ffe7bd" stroke="none" />
        </template>
      </g>
    </g>

    <!-- ===== reading arm (rule) ===== -->
    <g class="ring" :style="armStyle">
      <path d="M400 400 L400 52" class="s-magenta" stroke-width="1.3" />
      <path d="M400 400 L400 610" class="s-magenta" stroke-width="0.9" opacity="0.7" />
      <circle cx="400" cy="86" r="7" class="s-magenta" stroke-width="1.2" />
      <circle cx="400" cy="610" r="4" class="s-magenta" stroke-width="1" />
    </g>

    <!-- center -->
    <g class="s-gold">
      <circle cx="400" cy="400" r="10" stroke-width="1.2" />
      <path d="M400 384 L400 416 M384 400 L416 400" stroke-width="1" />
      <circle cx="400" cy="400" r="2.6" fill="#ffe7bd" stroke="none" />
    </g>
  </svg>
</template>

<style scoped>
.astrolabe {
  filter: drop-shadow(0 0 6px rgba(240, 167, 92, 0.35)) drop-shadow(0 0 26px rgba(124, 79, 224, 0.3));
}

.astrolabe path,
.astrolabe line,
.astrolabe circle,
.astrolabe ellipse {
  stroke-linecap: round;
  stroke-linejoin: round;
}

.s-gold { stroke: url(#astro-gold); }
.s-violet { stroke: #8b6ff0; }
.s-magenta { stroke: #d75fe8; }

/* rotations around the instrument's center, scrubbed by the parent */
.ring {
  transform-box: view-box;
  transform-origin: 400px 400px;
  will-change: transform;
}

.rune path {
  transition: stroke 0.6s ease;
}

.rune-active {
  filter: drop-shadow(0 0 10px rgba(215, 95, 232, 0.9));
}
</style>
